import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Sparkles, Info, RefreshCw } from 'lucide-react';

interface SmartSortIndicatorProps {
  scoringNotes?: string;
  scores?: Record<string, number>;
  usedAiFallback?: boolean;
  sessionDisabled?: boolean;
  onReplay?: () => void;
  isDev?: boolean;
}

export const SmartSortIndicator: React.FC<SmartSortIndicatorProps> = ({
  scoringNotes,
  scores,
  usedAiFallback,
  sessionDisabled,
  onReplay,
  isDev = false
}) => {
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  if (usedAiFallback) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="outline" className="text-xs text-muted-foreground border-muted">
              <Info className="w-3 h-3 mr-1" />
              Using local sort
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p>AI sort unavailable, using local sorting</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (sessionDisabled) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="outline" className="text-xs text-muted-foreground border-muted">
              <Info className="w-3 h-3 mr-1" />
              AI temporarily disabled
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p>AI sort temporarily disabled due to errors. Refresh to retry.</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const truncatedNotes = scoringNotes && scoringNotes.length > 100 
    ? scoringNotes.substring(0, 100) + '...' 
    : scoringNotes;

  return (
    <div className="flex items-center gap-2">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="secondary" className="text-xs bg-primary/10 text-primary border-primary/20">
              <Sparkles className="w-3 h-3 mr-1" />
              Re-ranked by Vergo AI
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p>{truncatedNotes || 'Results optimized by AI for your query'}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {(scoringNotes && scoringNotes.length > 100) || (scores && Object.keys(scores).length > 0) ? (
        <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
          <DialogTrigger asChild>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs">
              <Info className="w-3 h-3" />
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-base">AI Sort Details</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {scoringNotes && (
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-2">Reasoning</h4>
                  <p className="text-sm">{scoringNotes}</p>
                </div>
              )}
              
              {scores && Object.keys(scores).length > 0 && (
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-2">Confidence Scores</h4>
                  <div className="text-xs space-y-1">
                    {Object.entries(scores)
                      .slice(0, 5) // Show top 5
                      .map(([id, score]) => (
                        <div key={id} className="flex justify-between">
                          <span className="truncate flex-1 mr-2">{id}</span>
                          <span className="font-mono">{(score * 100).toFixed(0)}%</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      ) : null}

      {isDev && onReplay && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-6 px-2" 
                onClick={onReplay}
              >
                <RefreshCw className="w-3 h-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Replay last request (dev only)</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};